/**
 * CustomUser write endpoints for odl_app.
 *
 * Users are browsed via EntityGrid (dataSource/apiClient) over odl-app/users/,
 * so no grid list endpoint lives here; the one query below serves a pick-list
 * (the services crew form's member picker), not the grid. This module injects
 * the odl-app CustomUser read/write surfaces into entitiesApi and re-exports
 * the generated hooks the support/admin user pages drive: detail read, create,
 * patch, site-group-access grant, staff-or-self password set,
 * password-reset email, and the soft-delete/restore lifecycle (preflight +
 * apply) over the users/bulk/ actions.
 *
 * Most actions wrap the body in the odl-app { "data": … } envelope, so their
 * transformResponse is the plain `b => b.data`. The RPC and bulk actions are
 * BARE — set_password → { "status": … }, sendpasswordreset → { "success": true },
 * users/bulk/ → per-item id buckets — so they use `b => b.data ?? b` (getting
 * this wrong silently swallows success).
 *
 * Avatar upload/clear and user-agreement acceptance are deliberately NOT here:
 * they live on the settings/profile and self-service auth surfaces (legacy DRF
 * /api/users/<id>/ form), not this grid-admin write surface — see src/api/user.js
 * and features/auth/authActions.js.
 *
 * Tags invalidate the User LIST (+ the touched id) so any RTKQ read consumer
 * refetches; the EntityGrid list rides the MUI dataSource, which tag
 * invalidation does NOT reach — grid callers refresh via the onGridReady
 * `refresh` seam after a mutation resolves.
 *
 * Contains:
 * - usersApi      — the injected endpoint slice
 * - hook re-exports (see the named export block)
 */

import { buildCrewMemberUsersGrid } from '@sophocles/entities/User/crewMemberUsers';

import { entitiesApi } from './entitiesApi';

// Bulk lifecycle body: explicit ids, or select_all with the grid's JSON:API
// filter params so the backend acts on exactly the filtered set.
const lifecycleBody = (action, { userIds = [], selectAll = false, filters = {} }) =>
  selectAll ? { action, select_all: true, filters } : { action, user_ids: userIds };

const usersApi = entitiesApi.injectEndpoints({
  endpoints: build => ({
    /**
     * List the users eligible to staff a crew — active Technicians and Crew
     * Managers. Cached under the User LIST tag, so a user mutation refreshes
     * the picker's options automatically.
     */
    listCrewMemberUsers: build.query({
      query: () => ({ url: 'users/', grid: buildCrewMemberUsersGrid() }),
      transformResponse: b => b.data,
      providesTags: [{ type: 'User', id: 'LIST' }],
    }),

    /**
     * Retrieve a single user (ExpandedCustomUser shape).
     *
     * @param {{ id: string|number }} arg
     */
    retrieveUser: build.query({
      query: ({ id }) => ({ url: `users/${id}/` }),
      transformResponse: b => b.data,
      providesTags: (result, error, { id }) => [{ type: 'User', id }],
    }),

    /**
     * Create a user. Body is the CustomUser create input; returns detail (201).
     * Field-validation failures surface at error.data.errors.
     *
     * @param {{ body: object }} arg
     */
    createUser: build.mutation({
      query: ({ body }) => ({ url: 'users/', method: 'post', data: body }),
      transformResponse: b => b.data,
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),

    /**
     * Partial update a user (group membership, active flag, profile fields).
     *
     * @param {{ id: string|number, body: object }} arg
     */
    patchUser: build.mutation({
      query: ({ id, body }) => ({
        url: `users/${id}/`,
        method: 'patch',
        data: body,
      }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id: 'LIST' },
        { type: 'User', id },
      ],
    }),

    /**
     * Grant site-group access (echoes the updated user).
     *
     * @param {{ id: string|number, body: object }} arg
     */
    setUserSiteGroupAccess: build.mutation({
      query: ({ id, body }) => ({
        url: `users/${id}/setsitegroupaccess/`,
        method: 'post',
        data: body,
      }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id: 'LIST' },
        { type: 'User', id },
      ],
    }),

    /**
     * Staff-or-self password set (admin "set manual password"). BARE response
     * { status: 'password set' } — note the underscore in the path. 400 → the
     * field-keyed serializer errors arrive at error.data.
     *
     * @param {{ id: string|number, password: string }} arg
     */
    setUserPassword: build.mutation({
      query: ({ id, password }) => ({
        url: `users/${id}/set_password/`,
        method: 'post',
        data: { password },
      }),
      transformResponse: b => b.data ?? b,
    }),

    /**
     * Send the password-reset email for a user. BARE response { success: true }.
     *
     * @param {{ id: string|number }} arg
     */
    sendPasswordReset: build.mutation({
      query: ({ id }) => ({
        url: `users/${id}/sendpasswordreset/`,
        method: 'post',
        data: {},
      }),
      transformResponse: b => b.data ?? b,
    }),

    /**
     * Dry-run a soft-delete or restore. BARE response of id buckets —
     * delete: { to_delete, already_deleted, superuser_excluded, not_found,
     * self_excluded: bool }; restore: { to_restore, already_active, conflicts,
     * not_found }. Requires staff or customer-admin (403 otherwise).
     *
     * @param {{ action: 'delete'|'restore', userIds: number[] }} arg
     */
    preflightUserLifecycle: build.mutation({
      query: ({ action, ...target }) => ({
        url: 'users/bulk/preflight/',
        method: 'post',
        data: lifecycleBody(action, target),
      }),
      transformResponse: b => b.data ?? b,
    }),

    /**
     * Apply a soft-delete or restore. BARE response of id buckets — delete:
     * { deleted, already_deleted, superuser_excluded, not_found, failed:
     * [{ id, detail }], self_excluded: bool }; restore: { restored,
     * already_active, conflicts, not_found, failed }. Per-item outcomes,
     * including email conflicts, arrive with HTTP 200; only a self-only
     * delete, an unknown action or a missing id list is a 400.
     *
     * @param {{ action: 'delete'|'restore', userIds: number[] }} arg
     */
    applyUserLifecycle: build.mutation({
      query: ({ action, ...target }) => ({
        url: 'users/bulk/',
        method: 'post',
        data: lifecycleBody(action, target),
      }),
      transformResponse: b => b.data ?? b,
      invalidatesTags: (result, error, { userIds = [] }) => [
        { type: 'User', id: 'LIST' },
        ...userIds.map(id => ({ type: 'User', id })),
      ],
    }),
  }),
});

export { usersApi };
export const {
  useListCrewMemberUsersQuery,
  useRetrieveUserQuery,
  useLazyRetrieveUserQuery,
  useCreateUserMutation,
  usePatchUserMutation,
  useSetUserSiteGroupAccessMutation,
  useSetUserPasswordMutation,
  useSendPasswordResetMutation,
  usePreflightUserLifecycleMutation,
  useApplyUserLifecycleMutation,
} = usersApi;
